import React, { useEffect, useState, Suspense } from 'react';
import { TrialHeaderBackground } from '../trial6/header/TrialHeader';
import { useRouter } from '../../../commonv3/hooks/useRouter';
import { TrialResourceState, TrialSections } from '../trial6/state/TrialResourceState';
import { scrollToAnchor } from '../../../mainv2/utils/scroll';
import { TrialLpContext } from '../../contexts/trial';
import { toAnchor, V3Routes } from '../../routes';
import { LazyContainer } from '../../../commonv3/components/organisms/lazy/LazyContainer';
import { LoaderRow } from '../../components/atoms/layout/LoaderRow';
import { useObserver } from 'mobx-react-lite';
import { LoadingIndicator } from '../../components/molecules/trial/LoadingIndicator';
import { ConceptSection } from '../trial6/concept/ConceptSection';
import './trialLandingPage.module.scss';
import { TrialNavigation } from '../../components/atoms/trial/navigation/TrialNavigation';
import { GlobalStatesV3 } from '../../../commonv3/states/GlobalStatesV3';
import Banner from './images/nagoya_design_banner.jpg';
import { LazyImage } from '../../../commonv3/components/atoms/images/LazyImage';
import { ExperimentName } from '../../experiments/index';
import { observer } from 'mobx-react';
import { TrialLessonPageFirstView } from './trialLessonPageFirstView'

const createBottomSection = () => {
    const Component = React.lazy(() => import('./TrialLandingPageBottomSection'));
    const returnObject = (props) => (
        <Suspense fallback={<LoaderRow />}>
            <Component {...props} />
        </Suspense>
    );
    return (props) => returnObject(props);
};
const BottomSection = createBottomSection();

export const TRIAL8_HEADER_ITEMS = [
    {
        to: toAnchor(TrialSections.ABOUT),
        mainText: 'ABOUT',
        subText: 'SHElikesとは',
        longSubText: 'SHElikesについて',
    },
    {
        to: toAnchor(TrialSections.COURSE),
        mainText: 'COURSES',
        subText: 'コース紹介',
        longSubText: 'コース紹介',
    },
    {
        to: toAnchor(TrialSections.SUPPORT),
        mainText: 'SUPPORT',
        subText: 'サポート',
        longSubText: 'なりたい姿を叶えるためのサポート',
    },
    {
        to: toAnchor(TrialSections.VOICE),
        mainText: 'VOICES',
        subText: '受講者の声',
        longSubText: 'なりたい姿を叶えた受講者の声',
    },
    {
        to: toAnchor(TrialSections.FAQ),
        mainText: 'FAQ',
        subText: 'よくある質問',
        longSubText: 'よくある質問',
    },
    {
        to: toAnchor(TrialSections.MAP),
        mainText: 'MAP',
        subText: '拠点について',
        longSubText: 'SHEの拠点について',
    },
];

function calculateLoadPriority(sectionId: string) {
    switch (sectionId) {
        case TrialSections.CONCEPT:
            return 0;
        case TrialSections.COURSE:
            return 100;
        case TrialSections.MERIT:
            return 200;
        case TrialSections.PROCESS:
            return 300;
        case TrialSections.VOICE:
            return 400;
        case TrialSections.SCENES:
            return 500;
        case TrialSections.LESSONS:
            return 600;
        case TrialSections.FREE_CONSULTATION:
            return 700;
        case TrialSections.PLANS:
            return 800;
        case TrialSections.MAP:
            return 900;
        default:
            return 9999;
    }
}

export const TrialLandingPage = observer(() => {
    const [headerBackground, setHeaderBackground] = useState('transparent' as TrialHeaderBackground);
    const router = useRouter();
    const [trialLpContext] = useState(() => {
        return {
            serviceName: 'SHE',
            lessonName: '体験レッスン',
            resource: new TrialResourceState(calculateLoadPriority),
            reservationPagePath: V3Routes.trialLessons().pathname,
        };
    });
    const [showAll, setShowAll] = useState(() => {
        if (router.location.hash) {
            trialLpContext.resource.request('bottom', () => import('./TrialLandingPageBottomSection'));
        }
        return !!router.location.hash;
    });
    const [bottomSection, setBottomSection] = useState(null);
    const scrollWhenRendered = (sectionId) => {
        if (!trialLpContext.resource.isAllRendered()) {
            setTimeout(() => scrollWhenRendered(sectionId), 1000);
            return;
        }
        scrollToAnchor(sectionId);
    };
    const handleScroll = async (nextHash) => {
        if (nextHash) {
            if (!showAll) {
                setShowAll(true);
                return;
            }
            if (bottomSection) {
                const sectionId = nextHash.substr(1);
                scrollWhenRendered(sectionId);
            }
        }
    };
    useEffect(() => {
        trialLpContext.resource.start();
    }, []);
    useEffect(
        () => {
            handleScroll(router.location.hash);
        },
        [bottomSection, router.location.hash]
    );
    const handleConceptVisibilityChange = (newState) => {
        if (newState === 'BELOW_VIEWPORT_TOP') {
            setHeaderBackground('transparent');
        } else {
            setHeaderBackground('white');
        }
    };
    const isOptimizeTestUrl =
        GlobalStatesV3.experiments.getResolvedBucketId(ExperimentName.CHANGE_TOP_PAGE_KEY_VISUAL) === '1';

    const suffix = location.pathname.endsWith('/') ? '/' : '';
    const isDocumentLp = location.pathname === V3Routes.trialDocument().pathname + suffix;

    return (
        <div styleName="trial-landing-page">
            <TrialLpContext.Provider value={trialLpContext}>
                <TrialNavigation
                    background={headerBackground}
                    headerItems={TRIAL8_HEADER_ITEMS}
                    topUrl={V3Routes.trial('top')}
                />
                <TrialLessonPageFirstView
                    isOptimizeTestUrl={isOptimizeTestUrl}
                    isDocumentLp={isDocumentLp}
                />

                <ConceptSection onVisibilityChange={handleConceptVisibilityChange} />
                <LazyContainer
                    forceShow={showAll}
                    onEnter={() => {
                        setShowAll(true);
                    }}
                    fallback={<LoaderRow />}
                >
                    <BottomSection
                        setRef={(ref) => {
                            trialLpContext.resource.reportRender('bottom');
                            setBottomSection(ref);
                        }}
                    />
                </LazyContainer>
                {useObserver(() => {
                    const { started, allCompleted } = trialLpContext.resource;
                    return <LoadingIndicator show={started && !allCompleted} />;
                })}

                <a href={'https://likes.she-inc.jp/trial/nagoyadesign/'} target="_blank" className="nagoya-design-link">
                    <LazyImage src={Banner} styleName="nagoya-design-banner" />
                </a>
            </TrialLpContext.Provider>
        </div>
    );
});

export default TrialLandingPage;
